/**
 * FFmpeg.wasm 封装 Hook
 * 用于在浏览器中提取视频音频
 */

import { useState, useCallback, useRef } from 'react'
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'

const BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'

export function useFFmpeg() {
  const ffmpegRef = useRef(null)
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (loaded) return true
    setLoading(true)
    setError(null)

    try {
      const ffmpeg = new FFmpeg()
      ffmpegRef.current = ffmpeg

      ffmpeg.on('log', ({ message }) => {
        console.log('FFmpeg:', message)
      })

      ffmpeg.on('progress', ({ progress, time }) => {
        setProgress(`处理中... ${Math.round(progress * 100)}%`)
      })

      await ffmpeg.load({
        coreURL: `${BASE_URL}/ffmpeg-core.js`,
        wasmURL: `${BASE_URL}/ffmpeg-core.wasm`,
      })

      setLoaded(true)
      return true
    } catch (err) {
      setError('FFmpeg 加载失败: ' + err.message)
      return false
    } finally {
      setLoading(false)
    }
  }, [loaded])

  /**
   * 从视频文件中提取音频为 MP3
   */
  const extractAudio = useCallback(
    async (videoFile) => {
      if (!ffmpegRef.current || !loaded) {
        const ok = await load()
        if (!ok) return null
      }

      const ffmpeg = ffmpegRef.current
      setLoading(true)
      setProgress('正在提取音频...')
      setError(null)

      try {
        const inputName = 'input_video' + getExtension(videoFile.name)
        const outputName = 'output_audio.mp3'

        // 写入文件
        await ffmpeg.writeFile(inputName, await fetchFile(videoFile))

        // 提取音频: -vn 禁用视频, -ar 16000 采样率适合 Whisper, -ac 1 单声道
        await ffmpeg.exec([
          '-i', inputName,
          '-vn',
          '-ar', '16000',
          '-ac', '1',
          '-b:a', '32k',
          '-f', 'mp3',
          outputName,
        ])

        // 读取输出
        const data = await ffmpeg.readFile(outputName)
        const audioBlob = new Blob([data.buffer], { type: 'audio/mp3' })

        // 清理
        await ffmpeg.deleteFile(inputName).catch(() => {})
        await ffmpeg.deleteFile(outputName).catch(() => {})

        return audioBlob
      } catch (err) {
        setError('音频提取失败: ' + err.message)
        return null
      } finally {
        setLoading(false)
        setProgress('')
      }
    },
    [loaded, load]
  )

  /**
   * 将视频与新的字幕和/或音频合并
   * 简化版：烧录硬字幕到视频中
   */
  const mergeVideoWithSubtitles = useCallback(
    async (videoFile, srtContent, audioBlob = null) => {
      if (!ffmpegRef.current || !loaded) {
        const ok = await load()
        if (!ok) return null
      }

      const ffmpeg = ffmpegRef.current
      setLoading(true)
      setProgress('正在合成视频...')
      setError(null)

      try {
        const inputName = 'input_video' + getExtension(videoFile.name)
        const srtName = 'subtitles.srt'
        const outputName = 'output_video.mp4'

        await ffmpeg.writeFile(inputName, await fetchFile(videoFile))
        await ffmpeg.writeFile(srtName, new TextEncoder().encode(srtContent))

        const args = [
          '-i', inputName,
          '-vf', `subtitles=${srtName}:force_style='FontSize=24,PrimaryColour=&H00FFFFFF,OutlineColour=&H00000000,Outline=2,BorderStyle=1'`,
          '-c:a', 'copy',
          '-y',
          outputName,
        ]

        // 如果有新音频，替换原音频
        if (audioBlob) {
          const audioName = 'new_audio.mp3'
          await ffmpeg.writeFile(audioName, await fetchFile(audioBlob))
          args.splice(2, 0, '-i', audioName, '-map', '0:v:0', '-map', '1:a:0', '-shortest')
          args.splice(args.indexOf('-c:a'), 2, '-c:a', 'aac', '-b:a', '128k')
        }

        await ffmpeg.exec(args)

        const data = await ffmpeg.readFile(outputName)
        const videoBlob = new Blob([data.buffer], { type: 'video/mp4' })

        // 清理
        await ffmpeg.deleteFile(inputName).catch(() => {})
        await ffmpeg.deleteFile(srtName).catch(() => {})
        await ffmpeg.deleteFile(outputName).catch(() => {})
        if (audioBlob) await ffmpeg.deleteFile('new_audio.mp3').catch(() => {})

        return videoBlob
      } catch (err) {
        setError('视频合成失败: ' + err.message)
        return null
      } finally {
        setLoading(false)
        setProgress('')
      }
    },
    [loaded, load]
  )

  return {
    loaded,
    load,
    extractAudio,
    mergeVideoWithSubtitles,
    loading,
    progress,
    error,
  }
}

function getExtension(filename) {
  const ext = filename.lastIndexOf('.')
  return ext !== -1 ? filename.slice(ext) : '.mp4'
}